/**
 * The filter row above the requirement table, plus the chips that make the
 * active narrowing visible.
 *
 * BUILT ON THE SHARED WORKSPACE KIT rather than beside it: `BexioFilterBar`,
 * `BexioFilterSelect` and `BexioActiveFilters` already are the search box, the
 * labelled select and the chip strip this page needs, with their focus rings,
 * their reset affordance and their accessible names. A second copy under a
 * different prefix would be the same row with its own bugs. Nothing here
 * changes those components — every prop passed is one they already take.
 *
 * NO STATUS DROPDOWN. Status is chosen by pressing the card that counts it, so
 * the number and the list it opens are the same query. A select repeating those
 * six values would be a second control for one piece of state, and the two
 * would drift the first time either grew a value. The chip below is how an
 * active status filter stays visible and removable.
 */
import React from "react";

import BexioFilterBar, {
  BexioFilterSelect,
} from "../../../components/admin/bexio/workspace/BexioFilterBar";
import BexioActiveFilters from "../../../components/admin/bexio/workspace/BexioActiveFilters";
import {
  REQUIREMENT_TYPE_OPTIONS,
  requirementStatusLabel,
  requirementTypeLabel,
} from "./requirementLabels";

export default function RequirementFilters({
  search,
  onSearchChange,
  requirementType,
  onRequirementTypeChange,
  includeSuspended,
  onIncludeSuspendedChange,
  status,
  onStatusChange,
  onReset,
  canReset,
  resultLabel,
}) {
  const chips = [
    status
      ? {
          key: "status",
          label: `Status: ${requirementStatusLabel(status)}`,
          onRemove: () => onStatusChange(null),
        }
      : null,
    requirementType
      ? {
          key: "type",
          label: `Nachweistyp: ${requirementTypeLabel(requirementType)}`,
          onRemove: () => onRequirementTypeChange(""),
        }
      : null,
    search
      ? {
          key: "search",
          label: `Suche: ${search}`,
          onRemove: () => onSearchChange(""),
        }
      : null,
    includeSuspended
      ? {
          key: "suspended",
          label: "Ausgesetzte eingeblendet",
          onRemove: () => onIncludeSuspendedChange(false),
        }
      : null,
  ];

  return (
    <div className="flex flex-col gap-2">
      <BexioFilterBar
        searchValue={search}
        onSearchChange={onSearchChange}
        searchLabel="Suche"
        searchPlaceholder="Mieter, Mietverhältnis, Objekt suchen …"
        searchHint="Sucht über Referenznummer, Mietername, E-Mail, Objekt und Zimmer."
        onReset={onReset}
        canReset={canReset}
        resultLabel={resultLabel}
      >
        <BexioFilterSelect
          label="Nachweistyp"
          value={requirementType}
          onChange={onRequirementTypeChange}
          options={REQUIREMENT_TYPE_OPTIONS}
        />

        {/* A checkbox rather than a third status value: "ausgesetzt einblenden"
            widens the population every card counts, while the Ausgesetzt card
            narrows the list to it. Two different questions, two controls. */}
        <label className="inline-flex shrink-0 items-center gap-2 rounded-lg border border-border bg-background px-2.5 py-2 text-xs text-muted-foreground">
          <input
            type="checkbox"
            checked={includeSuspended}
            onChange={(e) => onIncludeSuspendedChange(e.target.checked)}
            className="h-3.5 w-3.5"
          />
          Historische / ausgesetzte anzeigen
        </label>
      </BexioFilterBar>

      <BexioActiveFilters chips={chips} onClearAll={onReset} />
    </div>
  );
}
